import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

interface MagicLinkResponse {
  success: boolean;
  message: string;
  devCode?: string; // Present in dev mode for testing code entry
}

interface VerifyCodeResponse {
  success: boolean;
  jwtToken: string;
  sessionToken: string;
  redirectUrl: string;
}

interface VerifyMagicLinkResponse {
  success: boolean;
  token: string;
  sessionToken: string;
  user: {
    id: string;
    email: string;
    name: string;
    firmId: string;
    roles: string[];
  };
  firm: {
    id: string;
    name: string;
    size: string;
  };
  availableServices: Array<{
    id: string;
    name: string;
    provider: string;
    type: string;
  }>;
  redirectUrl: string;
}

interface UserProfile {
  user: {
    id: string;
    email: string;
    name: string;
    firmId: string;
    roles: string[];
    active: boolean;
    lastLoginAt?: string;
  };
  firm: {
    id: string;
    name: string;
    size: string;
    maxAttorneys: number;
    claudeSeats: number;
  };
  license?: {
    tier: string;
    validUntil: string;
    seats: number;
    claudeSeats: number;
    dailyTokenLimit: number;
    features: string[];
  };
  availableServices: Array<{
    id: string;
    name: string;
    provider: string;
    type: string;
  }>;
  jwtAge: number; // JWT age in seconds
  jwtToken?: string; // Only included when ?j parameter is used
}

// Generate a cryptographically secure nonce
function generateNonce(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// Send magic link
export function useRequestMagicLink() {
  return useMutation<MagicLinkResponse, Error, { email: string; redirectTo?: string }>({
    mutationFn: async ({ email, redirectTo }) => {
      const nonce = generateNonce();
      const redirectUrl = `${window.location.origin}${redirectTo || '/ai'}`; // Default to AI dashboard, or use specified destination

      // Store nonce for verification (localStorage so it works across tabs)
      // Safari may throw in private mode, handle gracefully
      try {
        localStorage.setItem('auth_nonce', nonce);
      } catch (error) {
        console.error('Failed to store auth nonce in localStorage:', error);
        throw new Error(
          'Cannot access browser storage. Please disable private browsing mode or allow storage for this site in Safari settings.',
        );
      }

      return api.post<MagicLinkResponse>('/api/auth/request-magic-link', {
        email,
        nonce,
        redirectUrl, // This will be returned after successful auth and used as final destination
      });
    },
  });
}

// Verify magic link token
export function useVerifyMagicLink() {
  const queryClient = useQueryClient();

  return useMutation<VerifyMagicLinkResponse, Error, { token: string; nonce?: string }>({
    retry: false, // Don't retry magic link verification
    mutationFn: async ({ token, nonce: providedNonce }) => {
      // Use provided nonce or get from localStorage
      const nonce = providedNonce || localStorage.getItem('auth_nonce');
      if (!nonce) {
        throw new Error('Missing authentication nonce');
      }

      const response = await api.post<VerifyMagicLinkResponse>('/api/auth/verify-magic-link', {
        token,
        nonce,
      });

      // Clear nonce after use (only if we got it from localStorage)
      if (!providedNonce) {
        localStorage.removeItem('auth_nonce');
      }

      return response;
    },
    onSuccess: (data) => {
      // Store user data (tokens are now in HTTP-only cookies)
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('firm', JSON.stringify(data.firm));
      localStorage.setItem('availableServices', JSON.stringify(data.availableServices));

      // Update query cache
      queryClient.setQueryData(['auth', 'me'], {
        user: data.user,
        firm: data.firm,
        availableServices: data.availableServices,
      });

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}

// Verify code
export function useVerifyCode() {
  const queryClient = useQueryClient();

  return useMutation<VerifyCodeResponse, Error, { email: string; code: string; nonce?: string }>({
    retry: false, // Don't retry code verification
    mutationFn: async ({ email, code, nonce: providedNonce }) => {
      // Use provided nonce or get from localStorage
      const nonce = providedNonce || localStorage.getItem('auth_nonce');
      if (!nonce) {
        throw new Error('Missing authentication nonce');
      }

      const response = await api.post<VerifyCodeResponse>('/api/auth/verify-code', {
        email,
        code,
        nonce,
      });

      // Clear nonce after use (only if we got it from localStorage)
      if (!providedNonce) {
        localStorage.removeItem('auth_nonce');
      }

      return response;
    },
    onSuccess: (_data) => {
      // Tokens are now stored in HTTP-only cookies by the server

      // Invalidate auth queries to refetch user data
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}

// Get current user profile
export function useCurrentUser() {
  return useQuery<UserProfile>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      return api.get<UserProfile>('/api/auth/me');
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // No enabled check needed - HTTP-only cookies handle authentication
  });
}

// Logout
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        // HTTP-only cookies are automatically sent
        await api.post('/api/auth/logout', {});
      } catch (error) {
        // Continue with logout even if API call fails
        console.error('Logout API call failed:', error);
      }

      // Clear remaining stored data (HTTP-only cookies are cleared by server)
      localStorage.removeItem('elevatedToken');
      localStorage.removeItem('user');
      localStorage.removeItem('firm');
      localStorage.removeItem('availableServices');
      localStorage.removeItem('auth_nonce');
    },
    onSettled: () => {
      // Clear all queries and redirect
      queryClient.clear();
      window.location.href = '/';
    },
  });
}

// Helper to check if user has a specific role
export function useHasRole(role: 'attorney' | 'admin' | 'superadmin'): boolean {
  const { data } = useCurrentUser();
  if (!data?.user) return false;

  if (role === 'attorney') {
    // Any authenticated user has attorney access
    return true;
  }

  if (role === 'admin') {
    // Admin or superadmin has admin access
    return data.user.roles.includes('admin') || data.user.roles.includes('superadmin');
  }

  if (role === 'superadmin') {
    // Only superadmin has superadmin access
    return data.user.roles.includes('superadmin');
  }

  return false;
}

// Helper to check if user is authenticated
export function useIsAuthenticated(): boolean {
  const { data } = useCurrentUser();
  return !!data?.user;
}
